import {
  IndexTable,
  IndexFilters,
  useSetIndexFiltersMode,
  useIndexResourceState,
  Text,
  Badge,
  Page,
  Pagination,
  Banner,
  Modal,
  Button,
  BlockStack,
  LegacyCard,
  ChoiceList,
  TextField,
  DatePicker,
  Select,
  IndexFiltersMode,
  Popover,
  Card,
  InlineStack,
  Icon,
  Link,
  Box,
} from "@shopify/polaris";
import { DeliveryIcon, ClipboardIcon, InfoIcon, ArrowDownIcon, ChevronDownIcon, ArrowRightIcon, ViewIcon, ChevronRightIcon, RefreshIcon, StarIcon } from "@shopify/polaris-icons";
import type { IndexFiltersProps } from "@shopify/polaris";
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGetBulkOrdersFromShopifyQuery } from "../store/store/slices/OrderRTK";
import { extractLastId } from "../utils/helpers";
import dayjs from "dayjs";

function ShopifyOrders() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [direction, setDirection] = useState<"next" | "previous">("next");
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [mergeModalActive, setMergeModalActive] = useState(false);
  const [bookModalActive, setBookModalActive] = useState(false);
  const [activePopoverId, setActivePopoverId] = useState<string | null>(null);

  // Filter states
  const [selected, setSelected] = useState(0);
  const [queryValue, setQueryValue] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<string[]>([]);
  const [fulfillmentStatus, setFulfillmentStatus] = useState<string[]>([]);
  const [sortSelected, setSortSelected] = useState(["order desc"]);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);

  // Date picker states 
  const [{ month, year }, setDate] = useState<any>({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [selectedDates, setSelectedDates] = useState<any>(null);

  // Debounced search query for API (backend expects req.query.query)
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const DEBOUNCE_MS = 400;

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQuery(queryValue.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [queryValue]);

  // Reset pagination when search query changes
  const prevDebouncedQueryRef = useRef(debouncedQuery);
  const hadNonEmptyQueryRef = useRef(false);
  useEffect(() => {
    if (debouncedQuery !== "") hadNonEmptyQueryRef.current = true;
  }, [debouncedQuery]);
  useEffect(() => {
    if (prevDebouncedQueryRef.current !== debouncedQuery) {
      prevDebouncedQueryRef.current = debouncedQuery;
      setCursor(undefined);
      setCursorHistory([]);
      setDirection("next");
    }
  }, [debouncedQuery]);

  const { data: bulkOrdersData, isLoading: isLoadingBulkOrders, error: bulkOrdersError, refetch: refetchBulkOrders, isFetching } =
    useGetBulkOrdersFromShopifyQuery({ cursor, direction, query: debouncedQuery }, { skip: false });

  // When user clears search, refetch so request runs (RTK Query otherwise serves cache for query "")
  useEffect(() => {
    if (debouncedQuery === "" && hadNonEmptyQueryRef.current && cursor === undefined) {
      hadNonEmptyQueryRef.current = false;
      refetchBulkOrders();
    }
  }, [debouncedQuery, cursor, refetchBulkOrders]);

 

  // Filter handlers
  const handlePaymentStatusChange = useCallback((value: string[]) => {
    setPaymentStatus(value);
  }, []);

  const handleFulfillmentStatusChange = useCallback((value: string[]) => {
    setFulfillmentStatus(value);
  }, []);

  const handleFiltersQueryChange = useCallback((value: string) => {
    setQueryValue(value);
  }, []);

  const handleMonthChange = useCallback(
    (month: number, year: number) => setDate({ month, year }),
    []
  );

  const handlePaymentStatusRemove = useCallback(() => setPaymentStatus([]), []);
  const handleFulfillmentStatusRemove = useCallback(() => setFulfillmentStatus([]), []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(""), []);
  const handleRemoveDate = useCallback(() => setSelectedDates(null), []);

  const handleFiltersClearAll = useCallback(() => {
    handlePaymentStatusRemove();
    handleFulfillmentStatusRemove();
    handleQueryValueRemove();
    handleRemoveDate();
  }, [
    handlePaymentStatusRemove,
    handleFulfillmentStatusRemove,
    handleQueryValueRemove,
    handleRemoveDate,
  ]);

  // Filters configuration - empty array to hide filter button
  const filters: IndexFiltersProps["filters"] = [];

  // Applied filters
  const appliedFilters: IndexFiltersProps["appliedFilters"] = [];
  if (fulfillmentStatus && fulfillmentStatus.length > 0) {
    appliedFilters.push({
      key: "fulfillment_status",
      label: `Fulfillment Status : ${fulfillmentStatus.join(", ")}`,
      onRemove: handleFulfillmentStatusRemove,
    });
  }
  if (paymentStatus && paymentStatus.length > 0) {
    appliedFilters.push({
      key: "payment_status",
      label: `Payment Status : ${paymentStatus.join(", ")}`,
      onRemove: handlePaymentStatusRemove,
    });
  }
  if (selectedDates) {
    appliedFilters.push({
      key: "created_date",
      label: `Created Date : ${selectedDates.start?.toDateString()}${selectedDates.end ? ` to ${selectedDates.end.toDateString()}` : ""}`,
      onRemove: handleRemoveDate,
    });
  }

  const isEmpty = (value: string | string[]): boolean => {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === "" || value == null;
    }
  };


  // Transform API data to orders format
  const orders = bulkOrdersData?.orders?.map((order: any) => {
    const customerName = order?.customer
      ? `${order.customer.firstName || ""} ${order.customer.lastName || ""}`.trim() || "N/A"
      : "N/A";

    const formattedDate = order?.createdAt
      ? dayjs(order.createdAt).format("MMM D, YYYY [at] h:mm A")
      : "N/A";

    const paymentStatus = order?.fullyPaid ? (
      <Badge progress='complete'>Paid</Badge>
    ) : order?.unpaid ? (
      <Badge progress='incomplete'>Unpaid</Badge>
    ) : (
      <Badge progress='partiallyComplete'>Partially paid</Badge>
    );

    const fulfillmentStatus = order?.displayFulfillmentStatus === "FULFILLED" ? (
      <Badge progress='complete' tone='success'>{order?.displayFulfillmentStatus}</Badge>
    ) : (
      <Badge progress='incomplete' tone={order?.displayFulfillmentStatus === "UNFULFILLED" ? "attention-strong" : "new"}>{order?.displayFulfillmentStatus}</Badge>
    );

    // Extract tracking number from fulfillments
    const trackingNumber = order?.fulfillments[0]?.trackingInfo[0]?.number || null;

    return {
      id: order?.id || "",
      orderName: order?.name || "",
      displayFulfillmentStatus: order?.displayFulfillmentStatus || "",
      trackingNumber,
      courierCompany: order?.fulfillments[0]?.trackingInfo[0]?.company || null,
      order: (
        <Text as='span' variant='bodyMd' fontWeight='semibold'>
          {order?.name || "N/A"}
        </Text>
      ),
      date: formattedDate,
      customer: customerName,
      paymentStatus,
      fulfillmentStatus,
    };
  }) || [];

  const resourceName = {
    singular: "order",
    plural: "orders",
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(orders);

  const handleNextPage = useCallback(() => {
    if (bulkOrdersData?.pageInfo?.hasNextPage && bulkOrdersData?.pageInfo?.endCursor) {
      setCursorHistory((prev) => [...prev, cursor || ""]);
      setCursor(bulkOrdersData.pageInfo.endCursor);
      setDirection("next");
    }
  }, [bulkOrdersData, cursor]);

  const handlePreviousPage = useCallback(() => {
    if (cursorHistory.length > 0) {
      const previousCursors = [...cursorHistory];
      previousCursors.pop(); // Remove last cursor
      const newCursor = previousCursors[previousCursors.length - 1] || undefined;
      setCursorHistory(previousCursors);
      setCursor(newCursor);
      setDirection("previous");
    } else {
      setCursor(undefined);
      setDirection("next");
    }
  }, [cursorHistory]);

  const handleMergeSelected = useCallback(() => {
    setMergeModalActive(true);
  }, []);

  const handleBookSelected = useCallback(() => {
    setBookModalActive(true);
  }, []);

  const handleMergeConfirm = useCallback(() => {
    setMergeModalActive(false);
    if (selectedResources.length > 0) {
      // Extract numeric IDs from GraphQL format if needed
      const numericIds = selectedResources.map((id: string) => extractLastId(id) || id);
      // Build query string with ids[] format
      const queryParams = numericIds.map((id: string) => `ids[]=${encodeURIComponent(id)}`).join('&');
      navigate(`/dashboard/merge-orders?${queryParams}`);
    }
  }, [selectedResources, navigate]);

  const handleBookConfirm = useCallback(() => {
    setBookModalActive(false);
    // TODO: Implement book logic
  }, [selectedResources]);

  const handleBookOrder = useCallback((orderId: string) => {
    // Extract numeric ID from GraphQL format if needed (e.g., gid://shopify/Order/6324694089846 -> 6324694089846)
    const numericId = extractLastId(orderId) || orderId;
    navigate(`/dashboard/order/book?id=${numericId}`);
  }, [navigate]);

  const handleTrackOrder = useCallback((trackingNumber: string) => {
    if (trackingNumber) {
      navigate(`/track/${trackingNumber}`);
    }
  }, [navigate]);

  const hasSelectedOrders = selectedResources.length > 0;

  const rowMarkup = orders.map(
    (
      { id, orderName, displayFulfillmentStatus, trackingNumber, order, date, customer, paymentStatus, fulfillmentStatus, courierCompany }: any,
      index: number
    ) => {
      const isUnfulfilled = displayFulfillmentStatus === "UNFULFILLED";

      return (
        <IndexTable.Row
          id={id}
          key={id}
          selected={selectedResources.includes(id)}
          position={index}
        >
          <IndexTable.Cell>
            <p style={{ maxWidth: "179px", textWrap: "wrap" }}>
              <Text variant='bodyMd' fontWeight='bold' as='span'>
                {order}
              </Text>
            </p>
          </IndexTable.Cell>
          <IndexTable.Cell>{date}</IndexTable.Cell>
          <IndexTable.Cell>{customer}</IndexTable.Cell>
          <IndexTable.Cell>{paymentStatus}</IndexTable.Cell>
          <IndexTable.Cell>{fulfillmentStatus}</IndexTable.Cell>
          <IndexTable.Cell>
            {!isUnfulfilled ? (
              <Popover
                active={activePopoverId === id}
                activator={
                  <button
                    type="button"
                    onClick={() => setActivePopoverId(activePopoverId === id ? null : id)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                  >
                    <Badge progress="complete" size="small">
                      {<InlineStack align="space-between" gap="0">
                        <Text as="p" variant="bodyMd" tone="subdued">Tracking added</Text>
                        <Icon source={ChevronDownIcon} tone="inherit" />
                      </InlineStack> as any}
                    </Badge>
                  </button>
                }
                onClose={() => setActivePopoverId(null)}
                preferredPosition="below"
                preferredAlignment="left"
              >
                <Box padding="400">
                  <BlockStack gap="400">
                    <InlineStack align="space-between" gap="400">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">{orderName}</Text>
                      <Text as="p" variant="bodyMd" tone="subdued">Tracking added </Text>
                    </InlineStack>
                    <InlineStack align="start" gap="400">
                      <Icon source={DeliveryIcon} tone="inherit" />
                      <BlockStack gap="0">
                        <Link url={`/track/${trackingNumber}`} external>
                          {trackingNumber}
                        </Link>
                        {courierCompany && (
                          <Text as="p" variant="bodyMd" tone="subdued">{courierCompany}</Text>
                        )}
                      </BlockStack>
                      <Button
                        variant="plain"
                        onClick={() => {
                          navigator.clipboard.writeText(trackingNumber || "");
                          setActivePopoverId(null);
                        }}
                        size="micro"
                        icon={ClipboardIcon}
                      />
                    </InlineStack>
                  </BlockStack>
                </Box>
              </Popover>
            ) : (
              null
            )}
          </IndexTable.Cell>
          <IndexTable.Cell>
            <InlineStack gap="500">
              {isUnfulfilled ? (
                <Button onClick={() => handleBookOrder(id)} size="micro" variant="primary">
                  Book this order
                </Button>
              ) : (
                <Button
                  onClick={() => handleTrackOrder(trackingNumber)}
                  size="micro"
                  disabled={!trackingNumber}
                >
                  Track this order
                </Button>
              )}
              <Link url={`/dashboard/tracking${orderName ? `?orderName=${encodeURIComponent(orderName)}` : ''}`}>
                <InlineStack align="space-between" gap="0">View details
                  <Icon source={ChevronRightIcon} tone="inherit" />
                </InlineStack>
              </Link>
            </InlineStack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    }
  );

  return (
    <Page title='Shopify Orders' backAction={{ onAction: () => navigate(-1) }} additionalMetadata={<Badge tone="attention">Unfulfilled orders</Badge>} fullWidth secondaryActions={hasSelectedOrders ? <Button onClick={handleMergeSelected} > 
        Merge selected order(s)
      </Button> : undefined} primaryAction={<Button onClick={refetchBulkOrders} loading={isFetching} icon={RefreshIcon}>
        Refresh
      </Button>}>
      {bulkOrdersError && (
        <Banner tone="critical" onDismiss={() => { }}>
          <p>Failed to load orders. Please try again.</p>
        </Banner>
      )}

      <BlockStack gap='200'>
        <LegacyCard>
          <IndexFilters
            sortOptions={[]}
            sortSelected={sortSelected}
            queryValue={queryValue}
            queryPlaceholder='Search by order id, customer name, etc.'
            onQueryChange={handleFiltersQueryChange}
            onQueryClear={() => setQueryValue("")}
            onSort={setSortSelected}
            tabs={[]}
            selected={selected}
            loading={isLoadingBulkOrders || isFetching}
            onSelect={setSelected}
            canCreateNewView={false}
            filters={filters}
            appliedFilters={appliedFilters}
            onClearAll={handleFiltersClearAll}
            autoFocusSearchField={false}
            mode={mode}
            setMode={setMode}
            borderlessQueryField
          />
        </LegacyCard>
        <LegacyCard>
          <IndexTable
            resourceName={resourceName}
            loading={isLoadingBulkOrders}
            itemCount={isLoadingBulkOrders ? 1 : orders.length}
            selectedItemsCount={
              allResourcesSelected ? "All" : selectedResources.length
            }
            onSelectionChange={handleSelectionChange}
            headings={[
              {
                title: "Order id",
                tooltipContent: (
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      lineHeight: 1,
                    }}
                  >
                    Order id given by Shopify
                  </p>
                ),
                tooltipWidth: "default",
              },
              {
                title: "Created date",
                tooltipContent: (
                  <p
                    style={{
                      textAlign: "center",
                      fontSize: "14px",
                      lineHeight: 1,
                    }}
                  >
                    When order was created
                  </p>
                ),
                tooltipWidth: "default",
              },
              {
                title: "Customer",
              },
              {
                title: "Payment status",
              },
              {
                title: "Fulfillment status",
              },
              {
                title: "Delivery status",
              },
              {
                title: "Actions",
              },
            ]}
          >
            {rowMarkup}
          </IndexTable>
          {orders.length > 0 && !isLoadingBulkOrders && (
            <div style={{ padding: "1rem", display: "flex", justifyContent: "center", background: "#F7F7F7" }} className="table-bottom-banner">
              <Pagination
                hasPrevious={cursorHistory.length > 0 || (bulkOrdersData?.pageInfo?.hasPreviousPage || false)}
                onPrevious={handlePreviousPage}
                hasNext={bulkOrdersData?.pageInfo?.hasNextPage || false}
                onNext={handleNextPage}
                label={`Showing ${orders.length} orders`}
              />
            </div>
          )}
        </LegacyCard>
      </BlockStack>
      <Modal
        open={mergeModalActive}
        onClose={() => setMergeModalActive(false)}
        title="Merge Selected Orders"
        primaryAction={{
          content: "Confirm Merge",
          onAction: handleMergeConfirm,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setMergeModalActive(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              Are you sure you want to merge {selectedResources.length} selected order{selectedResources.length > 1 ? "s" : ""}?
            </Text>
            <Banner tone="warning">

              This action will combine the selected orders into a single order.
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={bookModalActive}
        onClose={() => setBookModalActive(false)}
        title="Book Selected Orders"
        primaryAction={{
          content: "Confirm Booking",
          onAction: handleBookConfirm,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setBookModalActive(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              Are you sure you want to book {selectedResources.length} selected order{selectedResources.length > 1 ? "s" : ""}?
            </Text>
            <Banner tone="warning">

              This action will book the selected orders for shipping. Each order will be booked for shipping separately.
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default ShopifyOrders;
